import { useId } from "react";
import { Icon } from "../../components/Icon";
import { GROUP_DIAL_REASON, fmtCompareDate } from "./compareModel";

/**
 * Look back, on the search row: the way into compare mode, and — while it is on — the way out. It sits
 * beside search rather than in the card's tab strip because it changes the whole workspace, not one pane.
 * Below 920px it shrinks to its icon; the state it would have said goes into the accessible name instead.
 */
export function LookBackButton({ active, day, disabled, onEnter, onLeave }: {
  active: boolean;
  day: string;
  /** Group dials: shown, but greyed out with the reason, rather than missing and unexplained. */
  disabled?: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const reasonId = useId();
  const date = fmtCompareDate(day);

  if (disabled) {
    return (
      <>
        <button
          type="button"
          className="kd-lookback-btn kd-focusable"
          aria-disabled="true"
          aria-describedby={reasonId}
          title={GROUP_DIAL_REASON}
          onClick={(e) => e.preventDefault()}
          data-state="disabled"
        >
          <Icon name="history" size={18} />
          <span className="kd-lookback-label">Look back</span>
        </button>
        <span id={reasonId} hidden>{GROUP_DIAL_REASON}</span>
      </>
    );
  }

  if (!active) {
    return (
      <button
        type="button"
        className="kd-lookback-btn kd-focusable"
        aria-pressed={false}
        aria-label="Look back"
        onClick={onEnter}
        data-state="off"
      >
        <Icon name="history" size={18} />
        <span className="kd-lookback-label">Look back</span>
      </button>
    );
  }

  // On: the whole pill says what's being compared, and the ✕ is its own control so leaving is never a guess.
  return (
    <div className="kd-lookback-btn" data-state="on">
      <button
        type="button"
        className="kd-lookback-main kd-focusable"
        aria-pressed={true}
        aria-label={`Comparing with ${date}`}
        onClick={onLeave}
      >
        <Icon name="history" size={18} />
        <span className="kd-lookback-label">Comparing with <strong>{date}</strong></span>
      </button>
      <span className="kd-lookback-divider" aria-hidden="true" />
      <button type="button" className="kd-lookback-close kd-focusable" aria-label="Leave compare mode" onClick={onLeave}>
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

/**
 * KEY-890: Look ahead, beside Look back — the dial as it will be if nothing is done. The same pill: off it opens the
 * mode looking forward; on it says which day, and its ✕ leaves.
 */
export function LookAheadButton({ active, day, disabled, onEnter, onLeave }: {
  active: boolean;
  day: string;
  disabled?: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const date = fmtCompareDate(day);
  if (!active) {
    return (
      <button
        type="button"
        className="kd-lookback-btn kd-focusable"
        aria-pressed={false}
        aria-label="Look ahead"
        aria-disabled={disabled ? "true" : undefined}
        title={disabled ? "Look ahead isn't available on group dials yet." : "How this dial will look on a later day if nothing is done"}
        onClick={(e) => (disabled ? e.preventDefault() : onEnter())}
        data-state={disabled ? "disabled" : "off"}
      >
        <Icon name="calendar" size={18} />
        <span className="kd-lookback-label">Look ahead</span>
      </button>
    );
  }
  return (
    <div className="kd-lookback-btn" data-state="on">
      <button type="button" className="kd-lookback-main kd-focusable" aria-pressed={true} aria-label={`Looking ahead to ${date}`} onClick={onLeave}>
        <Icon name="calendar" size={18} />
        <span className="kd-lookback-label">Looking ahead to <strong>{date}</strong></span>
      </button>
      <span className="kd-lookback-divider" aria-hidden="true" />
      <button type="button" className="kd-lookback-close kd-focusable" aria-label="Leave Look ahead" onClick={onLeave}>
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}
